import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

type FilterValue = string | number | boolean | Date | undefined;

interface UseFiltersOptions<T extends Record<string, FilterValue>> {
  defaultValues: T;
  syncWithUrl?: boolean;
}

interface UseFiltersReturn<T extends Record<string, FilterValue>> {
  filters: T;
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  setFilters: (newFilters: Partial<T>) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

export function useFilters<T extends Record<string, FilterValue>>({
  defaultValues,
  syncWithUrl = true,
}: UseFiltersOptions<T>): UseFiltersReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilters = (): T => {
    if (!syncWithUrl) return defaultValues;

    const filters = { ...defaultValues };
    Object.keys(defaultValues).forEach((key) => {
      const urlValue = searchParams.get(key);
      if (urlValue !== null) {
        const defaultValue = defaultValues[key];
        if (typeof defaultValue === 'number') {
          (filters as Record<string, FilterValue>)[key] = Number(urlValue);
        } else if (typeof defaultValue === 'boolean') {
          (filters as Record<string, FilterValue>)[key] = urlValue === 'true';
        } else if (defaultValue instanceof Date) {
          (filters as Record<string, FilterValue>)[key] = new Date(urlValue);
        } else {
          (filters as Record<string, FilterValue>)[key] = urlValue;
        }
      }
    });
    return filters;
  };

  const [filters, setFiltersState] = React.useState<T>(getInitialFilters);

  const updateUrl = React.useCallback(
    (newFilters: T) => {
      if (!syncWithUrl) return;

      setSearchParams((prev) => {
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === '' || value === defaultValues[key]) {
            prev.delete(key);
          } else if (value instanceof Date) {
            prev.set(key, value.toISOString());
          } else {
            prev.set(key, String(value));
          }
        });
        return prev;
      });
    },
    [syncWithUrl, setSearchParams, defaultValues]
  );

  const setFilter = React.useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setFiltersState((prev) => {
        const newFilters = { ...prev, [key]: value };
        updateUrl(newFilters);
        return newFilters;
      });
    },
    [updateUrl]
  );

  const setFilters = React.useCallback(
    (newFilters: Partial<T>) => {
      setFiltersState((prev) => {
        const merged = { ...prev, ...newFilters };
        updateUrl(merged);
        return merged;
      });
    },
    [updateUrl]
  );

  const resetFilters = React.useCallback(() => {
    setFiltersState(defaultValues);
    if (syncWithUrl) {
      setSearchParams((prev) => {
        Object.keys(defaultValues).forEach((key) => {
          prev.delete(key);
        });
        return prev;
      });
    }
  }, [defaultValues, syncWithUrl, setSearchParams]);

  const hasActiveFilters = React.useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      const defaultValue = defaultValues[key];
      if (value === undefined || value === '') return false;
      if (value instanceof Date && defaultValue instanceof Date) {
        return value.getTime() !== defaultValue.getTime();
      }
      return value !== defaultValue;
    });
  }, [filters, defaultValues]);

  return {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    hasActiveFilters,
  };
}
